// LocalOutlierScatterChart.jsx
import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";
import "../BenFordStyle.css";

const LocalOutlierScatterChart = ({
  data = [],
  dep,
  groupBy = [],
  xLabel,
  yLabel,
}) => {
  const chartRef = useRef(null);
  const echartsInstanceRef = useRef(null);

  const normalizeCategory = (raw) => {
    if (raw === null || raw === undefined) return "No exception";
    const s = String(raw).trim();
    if (!s) return "No exception";
    const lowered = s.toLowerCase();
    if (lowered.includes("no") || lowered.includes("normal"))
      return "No exception";
    if (lowered.includes("mild")) return "Mild";
    if (lowered.includes("extreme")) return "Extreme";
    if (s === "0") return "No exception";
    if (s === "1") return "Mild";
    if (s === "2") return "Extreme";
    if (lowered.includes("ext")) return "Extreme";
    return "No exception";
  };

  const prepareSeriesData = (rawData) => {
    const buckets = {
      "No exception": [],
      Mild: [],
      Extreme: [],
    };

    // Determine which field to use for x-axis labels
    const getXValue = (row) => {
      // If groupBy is provided, use those fields
      if (groupBy && groupBy.length > 0) {
        for (const field of groupBy) {
          const value = row[field] || row[field?.toUpperCase?.()] || row[field?.toLowerCase?.()];
          if (value !== undefined && value !== null && value !== "") {
            return String(value);
          }
        }
      }
      
      // Fallback to common field names
      const fieldPriority = [
        'Project_name', 'Vendor_Name', 'Project', 'EBELN', 'LIFNR', 'ERNAM', 'MATNR'
      ];
      
      for (const field of fieldPriority) {
        const value = row[field] || row[field?.toUpperCase?.()] || row[field?.toLowerCase?.()];
        if (value !== undefined && value !== null && value !== "") {
          return String(value);
        }
      }
      
      // Final fallback - use index
      return null;
    };

    const xValuesMap = new Map();
    let xIndexCounter = 1;

    rawData.forEach((pt, idx) => {
      try {
        let y =
          pt.y ??
          (pt.row &&
            (pt.row[dep] ||
              pt.row[dep?.toUpperCase?.()] ||
              pt.row[dep?.toLowerCase?.()])) ??
          pt.y;

        if (typeof y === "string") {
          const parsed = parseFloat(y.replace(/,/g, ""));
          if (!Number.isNaN(parsed)) y = parsed;
        }

        // Get the row data
        const row = pt.row || pt;
        
        // Get x value - this could be Project_name, Vendor_Name, etc.
        let xValue = getXValue(row);
        
        // If no meaningful x value found, use index
        let x = idx + 1;
        let xLabelForTooltip = String(x);
        
        if (xValue) {
          // Use mapping to maintain consistent x positions
          if (!xValuesMap.has(xValue)) {
            xValuesMap.set(xValue, xIndexCounter++);
          }
          x = xValuesMap.get(xValue);
          xLabelForTooltip = xValue;
        }

        let rawCat =
          pt.category ??
          pt.Final_Category ??
          pt.FinalCATEGORY ??
          (row &&
            (row.Final_Category ??
              row.FinalCategory ??
              row.final_category ??
              row.Finalcategory));

        const cat = normalizeCategory(rawCat);

        const meta = { 
          original: pt, 
          idx,
          xLabel: xLabelForTooltip // Store the actual label for tooltip
        };
        buckets[cat].push([x, y, meta]);
      } catch (error) {
        console.error('Error processing data point:', error);
      }
    });

    // Store the xValues mapping for axis labels
    buckets.xValuesMap = xValuesMap;

    return buckets;
  };

  const buildChartOptions = (buckets) => {
    const seriesConfig = [
      {
        name: "No Exception",
        color: "#1E90FF",
        symbol: "circle",
        symbolSize: 12,
      },
      {
        name: "Mild",
        color: "#FFA500",
        symbol: "rect",
        symbolSize: 10,
      },
      {
        name: "Extreme",
        color: "#FF4500",
        symbol: "triangle",
        symbolSize: 12,
      },
    ];

    const categories = seriesConfig.map((s) => s.name);

    const series = seriesConfig.map((cfg) => ({
      name: cfg.name,
      type: "scatter",
      data: buckets[cfg.name] || [],
      symbol: cfg.symbol,
      symbolSize: cfg.symbolSize,
      itemStyle: {
        color: cfg.color,
        opacity: 0.9,
      },
      emphasis: { focus: "series" },
    }));

    const xAxisName =
      xLabel || (groupBy && groupBy.length ? groupBy.join(" | ") : "Group");
    const yAxisName = yLabel || dep || "Value";

    // Prepare x-axis labels from the mapping
    const xValuesMap = buckets.xValuesMap || new Map();
    const xAxisData = [];
    if (xValuesMap.size > 0) {
      const sortedEntries = Array.from(xValuesMap.entries()).sort((a, b) => a[1] - b[1]);
      sortedEntries.forEach(([label, index]) => {
        xAxisData[index - 1] = label; // Adjust for 1-based indexing
      });
    }

    return {
      backgroundColor: "#fff",
      tooltip: {
        trigger: "item",
        backgroundColor: "rgba(50,50,50,0.9)",
        textStyle: { color: "#fff" },
        formatter: (params) => {
          const meta = params.data ? params.data[2] : null;
          const row =
            meta && meta.original ? meta.original.row ?? meta.original : null;
          let html = `<div style="min-width:180px"><b>${params.seriesName}</b><br/>`;
          html += `<b>${xAxisName}:</b> ${meta?.xLabel || params.data[0]}<br/>`;
          html += `<b>${yAxisName}:</b> ${params.data[1]}<br/>`;
          if (row?.Final_Category)
            html += `<b>Final_Category:</b> ${row.Final_Category}<br/>`;
          if (row?.Project_name)
            html += `<b>Project:</b> ${row.Project_name}<br/>`;
          if (row?.Vendor_Name)
            html += `<b>Vendor:</b> ${row.Vendor_Name}<br/>`;
          html += `</div>`;
          return html;
        },
      },
      legend: {
        data: categories,
        top: 10,
        textStyle: { color: "#333", fontWeight: "bold" },
      },
      grid: {
        left: 70,
        right: 30,
        top: 60,
        bottom: 70,
      },
      xAxis: {
        type: "category",
        name: xAxisName,
        nameLocation: "middle",
        nameGap: 45,
        nameTextStyle: {
          color: "#000",
          fontWeight: "bold",
          fontSize: 14,
        },
        data: xAxisData, // Use the actual labels here
        axisLabel: {
          color: "#000",
          fontWeight: "bold",
          rotate: 45,
          fontSize: 12,
          formatter: (value) => {
            return value && value.length > 8 ? value.substring(0, 8) + "..." : value;
          },
        },
        axisLine: { lineStyle: { color: "#555" } },
        splitLine: { show: false },
      },
      yAxis: {
        type: "log",
        name: yAxisName,
        nameLocation: "middle",
        nameGap: 55,
        nameTextStyle: {
          color: "#000",
          fontWeight: "bold",
          fontSize: 14,
        },
        axisLine: { lineStyle: { color: "#555" } },
        axisLabel: {
          color: "#000",
          fontSize: 12,
          fontWeight: "bold",
          formatter: (val) => val.toLocaleString(),
        },
        splitLine: { show: true, lineStyle: { color: "#eee" } },
      },
      dataZoom: [
        { type: "slider", xAxisIndex: 0, height: 20, bottom: 15 },
        { type: "inside", xAxisIndex: 0 },
      ],
      series,
    };
  };

  useEffect(() => {
    if (!chartRef.current) return;
    const buckets = prepareSeriesData(data);
    const option = buildChartOptions(buckets);

    if (!echartsInstanceRef.current) {
      echartsInstanceRef.current = echarts.init(chartRef.current);
    }

    const instance = echartsInstanceRef.current;
    instance.setOption(option, true);

    const handleResize = () => instance.resize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [data, dep, groupBy, xLabel, yLabel]);

  useEffect(() => {
    return () => {
      if (echartsInstanceRef.current) {
        echartsInstanceRef.current.dispose();
        echartsInstanceRef.current = null;
      }
    };
  }, []);

  return (
    <div
      className="one-wrapper-BenFord"
      style={{ height: "100%", width: "100%", position: "relative" }}
    >
      <div
        ref={chartRef}
        style={{ height: "100%", width: "100%" }}
        data-testid="local-outlier-chart"
      />
    </div>
  );
};

export default LocalOutlierScatterChart;